import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Settings, Wallet, FileText, Landmark, ArrowLeft } from 'lucide-react';
import * as facturacion from '@/api/facturacion.api';
import type { Factura, EstadoDeFactura } from '@/api/facturacion.api';
import { DataTable, type Columna } from '@/components/datos/DataTable';
import { ConDetalle } from '@/components/datos/DetailDrawer';
import { EntityLink } from '@/components/datos/EntityLink';
import { useAbrirDetalle } from '@/components/datos/contexto-detalle';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { enRango, opcionesDe, sinFiltros, textoDeResultados, type Faceta } from '@/lib/superfiltro';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { hasRole } from '@conversia/shared-types';
import { useAuthStore } from '@/store/auth.store';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/hooks/useToast';
import { dinero, numero, fecha } from '@/lib/formato-tienda';
import { EmitirFactura } from '@/components/tienda/EmitirFactura';
import { AjustesDeFacturacion } from '@/components/tienda/AjustesDeFacturacion';
import { ImpuestosDgii } from '@/components/tienda/ImpuestosDgii';
import { Cotizaciones } from '@/components/tienda/Cotizaciones';

/**
 * FACTURAS — la lógica de FacturApp con la cara del panel.
 *
 * Una factura es el papel de una venta: se emite desde una venta (aquí o en
 * «Ventas»), y emitirla sobre un borrador lo confirma y descuenta existencia.
 *
 * Pendiente, pagada y anulada son los estados de SU venta, vistos desde el
 * papel: marcarla pagada cobra la venta, anularla devuelve la mercancía.
 * Vencida es una pendiente a la que se le pasó el día de pago.
 *
 * «Impuestos» es la otra cara de la misma pantalla: el mes ante la DGII.
 */

/** El tope de la lista en el servidor (`LIMITE_DE_FACTURAS`). Al llegar, se dice. */
const TOPE = 1000;

const VISTAS = [
  { id: 'todas', etiqueta: 'Todas' },
  { id: 'pendientes', etiqueta: 'Pendientes', ayuda: 'Emitidas que aún no se han pagado' },
  { id: 'vencidas', etiqueta: 'Vencidas', ayuda: 'Sin pagar y ya pasó su día' },
  { id: 'pagadas', etiqueta: 'Pagadas' },
  { id: 'anuladas', etiqueta: 'Anuladas' },
] as const;

const DE_VISTA: Record<string, EstadoDeFactura> = {
  pendientes: 'PENDIENTE',
  vencidas: 'VENCIDA',
  pagadas: 'PAGADA',
  anuladas: 'ANULADA',
};

const ESTADO: Record<EstadoDeFactura, { etiqueta: string; variant: 'warning' | 'success' | 'danger' }> = {
  PENDIENTE: { etiqueta: 'Pendiente', variant: 'warning' },
  VENCIDA: { etiqueta: 'Vencida', variant: 'danger' },
  PAGADA: { etiqueta: 'Pagada', variant: 'success' },
  ANULADA: { etiqueta: 'Anulada', variant: 'danger' },
};

export default function Facturas() {
  return (
    <ConDetalle>
      <Contenido />
    </ConDetalle>
  );
}

function Contenido() {
  const abrir = useAbrirDetalle();
  const toast = useToast();
  const qc = useQueryClient();
  // Los ajustes (RNC, ITBIS, NCF) son de quien administra el sistema: lo dice el servidor.
  const rol = useAuthStore((s) => s.user?.role);
  const configura = rol ? hasRole(rol, 'OWNER') : false;
  const [ajustes, setAjustes] = useState(false);
  // En estado y no en la dirección: navegar vuelve a la pantalla, y al emitir hay que quedarse
  // en la pestaña de la factura.
  const [nueva, setNueva] = useState(false);
  const { filtros, poner, ponerVarios } = useFiltrosEnUrl(
    { vista: 'todas', buscar: '', ver: '', pago: '', comprobante: '', desde: '', hasta: '' },
    { vista: VISTAS.map((v) => v.id), ver: ['impuestos', 'cotizaciones'] },
  );

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['facturas'],
    queryFn: facturacion.listarFacturas,
  });
  const facturas = useMemo(() => data ?? [], [data]);

  const pagar = useMutation({
    mutationFn: (id: string) => facturacion.pagarFactura(id),
    onSuccess: () => {
      toast.success('Factura marcada como pagada.');
      for (const k of ['facturas', 'factura', 'ventas', 'venta', 'tienda']) {
        void qc.invalidateQueries({ queryKey: [k] });
      }
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo'),
  });

  const ver = (f: Factura) =>
    abrir?.({ tipo: 'factura', id: f.id, etiqueta: f.numero, detalle: f.clienteNombre });

  const de = (e: EstadoDeFactura) => facturas.filter((f) => f.estado === e);
  const porCobrar = [...de('PENDIENTE'), ...de('VENCIDA')];
  const enImpuestos = filtros.ver === 'impuestos';
  const vivas = facturas.filter((f) => f.estado !== 'ANULADA');
  // Neto de lo devuelto, como en Ventas y en Finanzas: una sola cifra para lo mismo.
  const neto = (f: Factura) => Math.max(0, f.total - f.devuelto);
  const suma = (fs: Factura[]) => fs.reduce((a, f) => a + neto(f), 0);

  const filtradas = useMemo(() => {
    let salida = facturas;
    const estado = DE_VISTA[filtros.vista];
    if (estado) salida = salida.filter((f) => f.estado === estado);
    if (filtros.pago) salida = salida.filter((f) => (f.sale.formaDePago ?? '') === filtros.pago);
    if (filtros.comprobante) salida = salida.filter((f) => (f.ncfTipo ?? '') === filtros.comprobante);
    salida = salida.filter((f) => enRango(f.emitidaAt, filtros.desde, filtros.hasta));
    const q = filtros.buscar.trim().toLowerCase();
    if (q) {
      salida = salida.filter((f) =>
        [f.numero, f.clienteNombre, f.clienteDocumento, f.ncf, f.sale.numero]
          .filter(Boolean)
          .some((t) => t!.toLowerCase().includes(q)),
      );
    }
    return salida;
  }, [facturas, filtros.vista, filtros.buscar, filtros.pago, filtros.comprobante, filtros.desde, filtros.hasta]);

  // SUPERFILTRO (Josué, 14-oct): las pestañas de antes son la faceta «Estado»; se suman pago,
  // comprobante (solo sale si hay facturas con NCF) y fecha de emisión.
  const facetas: Faceta[] = [
    { clave: 'vista', titulo: 'Estado', todas: 'Todas', opciones: VISTAS.filter((v) => v.id !== 'todas').map((v) => ({ valor: v.id, etiqueta: v.etiqueta, cuenta: de(DE_VISTA[v.id]!).length })) },
    { clave: 'pago', titulo: 'Forma de pago', opciones: opcionesDe(facturas, (f) => f.sale.formaDePago) },
    { clave: 'comprobante', titulo: 'Comprobante', opciones: opcionesDe(facturas, (f) => f.ncfTipo) },
    { tipo: 'fechas', clave: 'fecha', titulo: 'Emitida', desde: 'desde', hasta: 'hasta', hastaHoy: true },
  ];

  const columnas: Columna<Factura>[] = [
    {
      id: 'numero',
      titulo: 'N°',
      fija: true,
      ancho: 'w-28',
      valor: (f) => f.numero,
      celda: (f) => (
        <EntityLink tipo="factura" id={f.id} etiqueta={f.numero} detalle={f.clienteNombre} mono sinIcono />
      ),
    },
    {
      id: 'fecha',
      titulo: 'Fecha',
      valor: (f) => f.emitidaAt,
      celda: (f) => <span className="text-slate-600 dark:text-slate-300">{fecha(f.emitidaAt)}</span>,
    },
    {
      id: 'cliente',
      titulo: 'Cliente',
      valor: (f) => f.clienteNombre,
      celda: (f) =>
        f.sale.clienteId ? (
          <EntityLink tipo="cliente" id={f.sale.clienteId} etiqueta={f.clienteNombre} />
        ) : (
          <span>{f.clienteNombre}</span>
        ),
    },
    {
      id: 'venta',
      titulo: 'Venta',
      valor: (f) => f.sale.numero,
      celda: (f) => <EntityLink tipo="venta" id={f.sale.id} etiqueta={f.sale.numero} mono sinIcono />,
    },
    {
      id: 'ncf',
      titulo: 'NCF',
      ocultaPorDefecto: true,
      valor: (f) => f.ncf ?? '',
      celda: (f) =>
        f.ncf ? <span className="font-mono text-xs">{f.ncf}</span> : <span className="text-slate-400">—</span>,
    },
    {
      id: 'estado',
      titulo: 'Estado',
      valor: (f) => f.estado,
      celda: (f) => <Badge variant={ESTADO[f.estado].variant}>{ESTADO[f.estado].etiqueta}</Badge>,
    },
    {
      id: 'itbis',
      titulo: 'ITBIS',
      numerica: true,
      ocultaPorDefecto: true,
      valor: (f) => f.itbis,
      celda: (f) => (f.itbis > 0 ? dinero(f.itbis) : <span className="text-slate-400">—</span>),
    },
    {
      id: 'total',
      titulo: 'Total',
      numerica: true,
      valor: (f) => f.total,
      celda: (f) =>
        f.devuelto > 0 ? (
          <span title={`${dinero(f.devuelto)} devuelto`}>
            <span className="text-slate-400 line-through">{dinero(f.total)}</span> {dinero(neto(f))}
          </span>
        ) : (
          dinero(f.total)
        ),
      total: (fs) => dinero(suma(fs.filter((f) => f.estado !== 'ANULADA'))),
    },
  ];

  // Las cotizaciones son la tercera cara de esta pantalla: papel de venta, pero sin venta todavía.
  if (filtros.ver === 'cotizaciones') return <Cotizaciones onVolver={() => poner('ver', undefined)} />;

  if (enImpuestos) {
    return (
      <div className="space-y-5">
        <ModuleHeader
          titulo="Impuestos"
          subtitulo="El mes ante la DGII: el ITBIS de lo facturado y lo cobrado, las retenciones y el 607."
          acciones={
            <Button variant="outline" onClick={() => poner('ver', undefined)}>
              <ArrowLeft className="h-4 w-4" />
              Volver a las facturas
            </Button>
          }
        />
        <ImpuestosDgii />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <ModuleHeader
        titulo="Facturas"
        subtitulo="Cada factura sale de una venta. Emitirla descuenta la existencia; anularla la devuelve."
        acciones={
          <>
            {configura && (
              <Button variant="outline" onClick={() => setAjustes(true)}>
                <Settings className="h-4 w-4" />
                Ajustes
              </Button>
            )}
            <Button variant="outline" onClick={() => poner('ver', 'cotizaciones')}>
              <FileText className="h-4 w-4" />
              Cotizaciones
            </Button>
            <Button variant="outline" onClick={() => poner('ver', 'impuestos')}>
              <Landmark className="h-4 w-4" />
              Impuestos
            </Button>
            <Button onClick={() => setNueva(true)}>
              <Plus className="h-4 w-4" />
              Nueva factura
            </Button>
          </>
        }
        kpis={
          <>
            <Kpi
              titulo="Facturado"
              valor={dinero(suma(vivas))}
              pie={`${vivas.length} factura${vivas.length === 1 ? '' : 's'}`}
              onClick={() => poner('vista', 'todas')}
            />
            <Kpi
              titulo="Pendiente de pago"
              valor={dinero(suma(porCobrar))}
              pie={
                de('VENCIDA').length > 0
                  ? `${porCobrar.length} por cobrar · ${de('VENCIDA').length} vencida${de('VENCIDA').length === 1 ? '' : 's'}`
                  : `${porCobrar.length} por cobrar`
              }
              tono={porCobrar.length > 0 ? 'malo' : 'normal'}
              bajarEsBueno
              onClick={() => poner('vista', 'pendientes')}
            />
            <Kpi
              titulo="Pagado"
              valor={dinero(suma(de('PAGADA')))}
              pie={`${de('PAGADA').length} pagada${de('PAGADA').length === 1 ? '' : 's'}`}
              onClick={() => poner('vista', 'pagadas')}
            />
            <Kpi
              titulo="Anuladas"
              valor={numero(de('ANULADA').length)}
              onClick={() => poner('vista', 'anuladas')}
            />
          </>
        }
      />

      {facturas.length >= TOPE && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
          Se muestran las {numero(TOPE)} facturas más recientes. Los totales de arriba son de esas, no
          de todas.
        </p>
      )}


      <DataTable
        filas={filtradas}
        columnas={columnas}
        idDe={(f) => f.id}
        cargando={isLoading}
        error={isError ? (error instanceof Error ? error.message : 'Error') : null}
        onReintentar={() => void refetch()}
        buscar={filtros.buscar}
        onBuscar={(v) => poner('buscar', v)}
        placeholderBuscar="Busca por número, cliente, RNC o NCF…"
        facetas={facetas}
        valoresDeFacetas={{ ...filtros, vista: filtros.vista === 'todas' ? undefined : filtros.vista }}
        onFacetas={ponerVarios}
        onLimpiarFiltros={() => ponerVarios(sinFiltros(facetas))}
        resultados={textoDeResultados(filtradas.length, facturas.length, ['factura', 'facturas'])}
        memoria="facturas"
        exportarComo="facturas"
        onAbrirFila={ver}
        accionesDeFila={[
          {
            etiqueta: 'Ver / imprimir',
            icono: <FileText className="h-3.5 w-3.5" />,
            onClick: ver,
          },
          {
            etiqueta: 'Marcar pagada',
            icono: <Wallet className="h-3.5 w-3.5" />,
            ocultaSi: (f) => f.estado !== 'PENDIENTE' && f.estado !== 'VENCIDA',
            onClick: (f) => pagar.mutate(f.id),
          },
        ]}
        vacio={{
          titulo: filtros.buscar ? `Nada con "${filtros.buscar}"` : 'Todavía no hay facturas',
          descripcion: 'Se emiten desde una venta. Al emitirla, la existencia se descuenta sola.',
          accion: <Button onClick={() => setNueva(true)}>Nueva factura</Button>,
        }}
      />

      {nueva && (
        <EmitirFactura
          onCerrar={() => setNueva(false)}
          onEmitida={(f) => {
            setNueva(false);
            ver(f);
          }}
        />
      )}

      {ajustes && <AjustesDeFacturacion onCerrar={() => setAjustes(false)} />}
    </div>
  );
}
